import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { CategorySelectMoleculeComponent } from 'src/app/shared/ui/molecules/category-select/category-select.component';
import {
  DynamicFilterDescriptor,
  DynamicFilterFieldComponent,
  DynamicFilterType,
  DynamicFilterValueChange,
} from 'src/app/shared/ui/molecules/dynamic-filter-field/dynamic-filter-field.component';

// Categoría/subcategoría del dominio de ofertas: solo se leen 'id'/'name'.
interface SelectableItem {
  id: string | number;
  name: string;
}

export interface FilterPanelValueChange extends DynamicFilterValueChange {
  type: DynamicFilterType;
}

@Component({
  selector: 'app-filter-panel',
  imports: [CategorySelectMoleculeComponent, DynamicFilterFieldComponent],
  template: `
    @if (showCategoryFinder()) {
    <div class="containerSelect d-none d-md-block" [class.filter-fixed]="filterScroll()" [class.filter-view]="filterView()">
      <div class="header-select">
        <h3>¡Hola {{ nameSesion() }}, encuentra la oferta ideal que tenemos para ti:</h3>
      </div>
      <div class="content-select">
        <div class="scroll-box-bar">
          <app-category-select
            selectId="sel-category"
            [categories]="categoriesActive()"
            [selectedValue]="selectedCategoryId()"
            [showPlaceholder]="showCategoryPlaceholder()"
            [placeholderDisabled]="categoryPlaceholderDisabled()"
            (selectionChange)="categorySelected.emit($event)" />

          @if (subCategories().length > 0) {
          <div id="subCategory">
            <p>{{ categoryDescSub() }}</p>
            <select name="subCat" id="sel-subCat" class="input" [value]="selectedSubCategoryId() ?? ''" (change)="subCategorySelected.emit($event)">
              @if (2 > contOptionChooseCat()) {
              <!-- [selected]: un placeholder disabled nunca queda seleccionado por defecto, el navegador mostraría la primera subcategoría sin disparar (change) -->
              <option disabled [selected]="!selectedSubCategoryId()"> Elige lo que necesites </option>
              }
              @for (sub of subCategories(); track sub.id) {
              <option value="{{ sub.id }}">{{ sub.name }}</option>
              }
            </select>
          </div>
          }

          @for (fil of filtersSel(); track fil.id + '_' + filtersResetKey()) {
            <app-dynamic-filter-field
              [class.filter-bool-field]="fil.type === 'bool'" 
              [filter]="fil" 
              [resetKey]="filtersResetKey()"
              (valueChange)="handleFilterChange(fil.type, $event)" />
          }

          @if (filtersSel().length > 0) {
          <div class="clean">
            <span class="clean-spacer" aria-hidden="true">&nbsp;</span>
            <div class="clean-row">
              <button (click)="cleanRequested.emit()">Limpiar filtros </button>
            </div>
          </div>
          }
        </div>
      </div>
    </div>
    }

    <div id="backdropFilter" [class.backdropFilter]="isBackDropFilter()"></div>
    <div class="box-filter d-block d-md-none" [class.hideFilter]="btnFilterM()">
      @if (!isBackDropFilter()) {
      <button id="btnMobile" class="btnFilter" (click)="viewToggled.emit()">
        <img src="assets/icons/home/Icono_filtros.svg" alt="ofertas ideales">Filtrar ofertas
      </button>
      }
      <div class="containerSelect" id="filterMobile" [class.showFilter]="isBackDropFilter()">
        <div class="headF">
          <hr>
          <label class="closeFilterM" (click)="viewToggled.emit()">&#10005;</label>
        </div>
        <div class="content-select">
          <div class="scroll-box-bar">
            <p>¿Qué estás buscando?</p>
            <select name="category" id="sel-category-mob" class="input" [value]="selectedCategoryId() ?? ''" (change)="categorySelected.emit($event)">
              @if (1 > contOptionChooseCat() && isIdSession()) {
              <option> Elige lo que necesites </option>
              }
              @if (2 > contOptionChooseCat() && !isIdSession()) {
              <option disabled [selected]="!selectedCategoryId()"> Elige lo que necesites </option>
              }
              @for (cat of categoriesActive(); track cat.id) {
              <option value="{{ cat.id }}">{{ cat.name }}</option>
              }
            </select>

            @if (subCategories().length > 0) {
            <div id="subCategory">
              <p>{{ categoryDescSub() }}</p>
              <select name="subCat" id="sel-subCat-mob" class="input" [value]="selectedSubCategoryId() ?? ''" (change)="subCategorySelected.emit($event)">
                @if (2 > contOptionChooseSubcat()) {
                <option> Elige lo que necesites </option>
                }
                @for (sub of subCategories(); track sub.id) {
                <option value="{{ sub.id }}">{{ sub.name }}</option>
                }
              </select>
            </div>
            }

            @for (fil of filtersSel(); track fil.id + '_' + filtersResetKey()) {
              @if (fil.type === 'bool') {
              <div class="mobile-bool-clean-row">
                <app-dynamic-filter-field class="filter-bool-field" [filter]="fil" [resetKey]="filtersResetKey()" (valueChange)="handleFilterChange(fil.type, $event)" />
                <div class="clean">
                  <button (click)="cleanRequested.emit()">Limpiar filtros </button>
                </div>
              </div>
              } @else {
              <app-dynamic-filter-field [filter]="fil" [resetKey]="filtersResetKey()" (valueChange)="handleFilterChange(fil.type, $event)" />
              }
            }
          </div>

          @if (filtersSel().length > 0 && !hasBoolFilter()) {
          <div class="clean">
            <button (click)="cleanRequested.emit()">Limpiar filtros </button>
          </div>
          }
        </div>
        <div>
          <button class="btnFilter" (click)="viewToggled.emit()">
            <img src="assets/icons/home/Icono_filtros.svg" alt="ofertas ideales">Aplicar filtros
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrl: './filter-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPanelComponent {
  readonly showCategoryFinder = input(false);
  readonly nameSesion = input('');
  readonly filterScroll = input(false);
  readonly filterView = input(false);

  readonly categoriesActive = input<SelectableItem[]>([]);
  readonly selectedCategoryId = input<string | number | null>(null);
  readonly showCategoryPlaceholder = input(false);
  readonly categoryPlaceholderDisabled = input(false);
  readonly isIdSession = input(false);

  readonly subCategories = input<SelectableItem[]>([]);
  readonly selectedSubCategoryId = input<string | number | null>(null);
  readonly categoryDescSub = input('');

  readonly contOptionChooseCat = input(0);
  readonly contOptionChooseSubcat = input(0);

  readonly filtersSel = input<ReadonlyArray<DynamicFilterDescriptor>>([]);
  readonly filtersResetKey = input(0);

  readonly isBackDropFilter = input(false);
  readonly btnFilterM = input(true);

  readonly categorySelected = output<Event>();
  readonly subCategorySelected = output<Event>();
  readonly filterValueChange = output<FilterPanelValueChange>();
  readonly cleanRequested = output<void>();
  readonly viewToggled = output<void>();

  // En mobile 'Limpiar filtros' se agrupa junto al filtro bool (100% Digital) cuando existe.
  protected readonly hasBoolFilter = computed(() => this.filtersSel().some(f => f.type === 'bool'));

  handleFilterChange(type: DynamicFilterType, change: DynamicFilterValueChange): void {
    this.filterValueChange.emit({ ...change, type });
  }
}
